import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeftRight, Inbox, Paperclip, Pencil, RefreshCw, ScanLine, Shield, Undo2, ZoomIn } from "lucide-react";
import { Link } from "react-router-dom";
import { EmptyState } from "@/components/EmptyState";
import { ReceiptThumbnail } from "@/components/ReceiptThumbnail";
import { BillRowActions } from "@/components/bills/BillAttachments";
import { formatDate } from "@/lib/formatDate";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { ReclaimBusinessAction } from "@/components/inbox/ReclaimBusinessAction";

function statusLabel(status) {
  switch (status) {
    case "booked":
      return <Badge className="bg-emerald-100 text-emerald-800" data-testid="receipt-status-badge-booked">Verbucht</Badge>;
    case "suggested":
      return <Badge className="bg-amber-100 text-amber-800" data-testid="receipt-status-badge-suggested">Vorschlag</Badge>;
    case "archived":
      return <Badge className="bg-slate-200 text-slate-600" data-testid="receipt-status-badge-archived">Archiviert</Badge>;
    case "deleted":
      return <Badge className="bg-rose-100 text-rose-700" data-testid="receipt-status-badge-deleted">🗑 Gelöscht</Badge>;
    default:
      return <Badge className="bg-slate-100 text-slate-700" data-testid="receipt-status-badge-unreviewed">Offen</Badge>;
  }
}

function taxRatePct(taxRate) {
  // tax_rate kann als Prozent (8.1) oder Dezimal (0.081) gespeichert sein
  const raw = Number(taxRate || 0);
  const pct = raw > 0 && raw < 1 ? raw * 100 : raw;
  return `${pct.toFixed(1)}%`;
}

// Iter284: Beilage (E-Mail-Bundle) eines Belegs herunterladen
async function downloadAttachment(r, a) {
  try {
    const res = await apiClient.get(`/receipts/${r._id}/attachments/${a.file_id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: a.content_type || "application/octet-stream" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = a.file_name || "beilage";
    link.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Beilage konnte nicht geladen werden");
  }
}

export function ReceiptsTable({
  receipts,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onZoom,
  attachmentActions,
  onOpenMerger,
  onCompareDuplicates,
  onPost,
  onReanalyze,
  onEditAccount,
  reanalyzingId,
  onRebook,
  onUnbook,
  unbookingId,
  onUnarchive,
  onRestore,
  onCreateAsset,
  onToggleCreditNote,
  onToInbox,
  onToInboxBooked,
  reclaimCompanies,
  onReclaimed,
}) {
  return (
    <Table data-testid="receipts-table">
      <TableHeader>
        <TableRow>
          <TableHead className="w-[40px]">
            <Checkbox
              checked={receipts.length > 0 && receipts.every((r) => selectedIds.has(r._id))}
              onCheckedChange={onToggleSelectAll}
              data-testid="receipts-bulk-select-all"
            />
          </TableHead>
          <TableHead>Datum</TableHead>
          <TableHead className="w-[60px]">Beleg</TableHead>
          <TableHead>Beschreibung</TableHead>
          <TableHead>Betrag</TableHead>
          <TableHead>MWST-Satz</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Duplikat</TableHead>
          <TableHead>Aktion</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {receipts.length === 0 && (
          <TableRow data-testid="receipts-empty-row">
            <TableCell colSpan={9} className="p-0">
              <EmptyState
                testid="receipts-empty-state"
                icon={<ScanLine className="h-5 w-5" />}
                title="Noch keine Belege"
                description="Laden Sie Quittungen oder Rechnungen hoch — OCR und KI extrahieren Betrag, MWST und Konto automatisch."
              />
            </TableCell>
          </TableRow>
        )}
        {receipts.map((r) => (
          <TableRow
            key={r._id}
            data-testid={`receipt-row-${r._id}`}
            data-highlight-id={r._id}
            className={selectedIds.has(r._id) ? "bg-violet-50/40" : ""}
          >
            <TableCell className="w-[40px]">
              <Checkbox
                checked={selectedIds.has(r._id)}
                onCheckedChange={() => onToggleSelect(r._id)}
                data-testid={`receipt-select-${r._id}`}
              />
            </TableCell>
            <TableCell>
              {formatDate(r.date)}
              {r.paid_date && r.paid_date !== r.date && (
                <div className="text-[11px] text-emerald-700 whitespace-nowrap" data-testid={`receipt-paid-date-${r._id}`}>
                  Bezahlt {formatDate(r.paid_date)}
                  {r.debited_date && r.debited_date !== r.paid_date && (
                    <span className="text-slate-500"> · Belastet {formatDate(r.debited_date)}</span>
                  )}
                </div>
              )}
            </TableCell>
            <TableCell>
              <ReceiptThumbnail receipt={r} onZoom={onZoom} />
            </TableCell>
            <TableCell>
              <div className="space-y-0.5">
                <div className="flex items-center gap-1.5">
                  <span>{r.description}</span>
                  {attachmentActions && (
                    <BillRowActions
                      docType="receipt"
                      bill={r}
                      onOpen={attachmentActions.onOpen}
                      loading={attachmentActions.loading}
                      onAddAttachment={attachmentActions.onAddAttachment}
                    />
                  )}
                </div>
                {r.is_credit_note && (
                  <span className="inline-flex items-center gap-1">
                    <Badge className={`${r.credit_of_receipt_id ? "bg-teal-50 text-teal-800 border-teal-200" : "bg-amber-50 text-amber-800 border-amber-300"} border text-[10px]`} data-testid={`receipt-credit-note-${r._id}`}
                      title={r.credit_of_receipt_id ? "Gutschrift/Rückgabe – mit dem ursprünglichen Kaufbeleg verknüpft" : "Als Gutschrift markiert – aber kein passender Kaufbeleg verknüpft. Wirklich eine Gutschrift?"}>
                      ↩ Gutschrift{r.credit_of_receipt_id ? " zum Kauf" : " · kein Kaufbeleg"}
                    </Badge>
                    {!r.journal_entry_id && onToggleCreditNote && (
                      <button type="button" onClick={() => onToggleCreditNote(r, false)} className="text-[10px] text-slate-500 underline-offset-2 hover:underline" title="Markierung aufheben – als normaler Beleg (Aufwand) behandeln" data-testid={`receipt-credit-note-unmark-${r._id}`}>
                        keine Gutschrift
                      </button>
                    )}
                  </span>
                )}
                {r.warranty_id && (
                  <Link to={`/warranties?highlight=${r.warranty_id}`} data-testid={`receipt-warranty-${r._id}`}
                    title={r.warranty_expiry ? `Garantie bis ${formatDate(r.warranty_expiry)} – im Garantiebereich öffnen` : "Garantie im Garantiebereich öffnen"}>
                    <Badge className="bg-violet-50 text-violet-800 border border-violet-200 text-[10px] inline-flex items-center gap-1 hover:bg-violet-100">
                      <Shield className="h-3 w-3" /> Garantie{r.warranty_expiry ? ` bis ${formatDate(r.warranty_expiry)}` : ""}
                    </Badge>
                  </Link>
                )}
                {(r.credit_notes || []).length > 0 && (
                  <Badge className="bg-teal-50 text-teal-800 border border-teal-200 text-[10px]" data-testid={`receipt-has-credit-${r._id}`}
                    title={(r.credit_notes || []).map((c) => `Rückgabe ${formatDate(c.date)}: ${Number(c.amount).toFixed(2)}`).join(" · ")}>
                    ↩ Rückgabe erhalten −{Number(r.credited_total || 0).toFixed(2)}
                  </Badge>
                )}
                {r.ai_verification?.status === "mismatch_unresolved" && (
                  <Badge
                    className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px]"
                    data-testid={`receipt-ai-verify-warn-${r._id}`}
                    title="Zwei KI-Analysen kamen zu unterschiedlichen Ergebnissen — bitte Betrag/Datum manuell prüfen"
                  >
                    ⚠️ KI-Prüfung: Abweichung
                  </Badge>
                )}
                {(r.ai_verification?.status === "confirmed" || r.ai_verification?.status === "mismatch_resolved") && (
                  <Badge
                    className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]"
                    data-testid={`receipt-ai-verify-ok-${r._id}`}
                    title={`Von ${(r.ai_verification.checked_by || []).length} KIs unabhängig geprüft`}
                  >
                    ✓ Mehrfach KI-geprüft
                  </Badge>
                )}
                {r.user_note && (
                  <div
                    className="inline-flex items-start gap-1 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 mt-1 max-w-md"
                    data-testid={`receipt-user-note-${r._id}`}
                    title="Notiz vom Uploader"
                  >
                    <span className="text-amber-600">📝</span>
                    <span className="italic">{r.user_note}</span>
                  </div>
                )}
                {(r.attachments?.length > 0) && !attachmentActions && (
                  <div className="flex flex-wrap gap-1 mt-1" data-testid={`receipt-attachments-${r._id}`}>
                    {r.attachments.map((a) => (
                      <button
                        key={a.file_id}
                        onClick={() => downloadAttachment(r, a)}
                        className="inline-flex items-center gap-1 text-[10px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                        title={`Beilage aus derselben E-Mail herunterladen: ${a.file_name}`}
                        data-testid={`receipt-attachment-${a.file_id}`}
                      >
                        <Paperclip className="h-2.5 w-2.5" />
                        <span className="max-w-[160px] truncate">{a.file_name}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {r.cost_center_name && (
                    <span
                      className="inline-flex items-center text-[10px] font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5"
                      data-testid={`receipt-cost-center-${r._id}`}
                      title="Kostenstelle (aus Notiz)"
                    >
                      🎯 {r.cost_center_name}
                    </span>
                  )}
                  {r.source === "whatsapp" && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                      📱 WhatsApp
                    </span>
                  )}
                  {r.category && (
                    <span
                      className="inline-flex items-center text-[10px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5"
                      title="KI-erkannte Beleg-Kategorie"
                      data-testid={`receipt-category-badge-${r._id}`}
                    >
                      {r.category}
                    </span>
                  )}
                  {r.auto_supplier_created && (
                    <span
                      className="inline-flex items-center text-[10px] font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5"
                      title="Lieferant wurde automatisch aus KI-Erkennung angelegt"
                      data-testid={`receipt-auto-supplier-badge-${r._id}`}
                    >
                      ✨ Auto-Lieferant
                    </span>
                  )}
                  {r.asset_id && (
                    <span
                      className="inline-flex items-center text-[10px] font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5"
                      title="Aus diesem Beleg wurde eine Anlage mit Auto-Abschreibung erstellt"
                      data-testid={`receipt-asset-created-badge-${r._id}`}
                    >
                      🏭 Anlage erfasst
                    </span>
                  )}
                </div>
                {r.asset_suggestion?.detected && !r.asset_id && (
                  <div
                    className="mt-1 flex items-center gap-2 rounded border border-teal-300 bg-teal-50 px-2 py-1"
                    data-testid={`receipt-asset-suggestion-${r._id}`}
                  >
                    <span className="text-[10px] font-medium text-teal-800">
                      🏭 KI: sieht nach {r.asset_suggestion.category_label} aus — als Anlage aktivieren?
                    </span>
                    <button
                      type="button"
                      onClick={() => onCreateAsset(r)}
                      className="text-[10px] font-semibold text-teal-700 underline hover:text-teal-900"
                      data-testid={`receipt-create-asset-btn-${r._id}`}
                    >
                      Als Anlage anlegen
                    </button>
                  </div>
                )}
                {r.suggested_account && r.status !== "booked" && r.status !== "archived" && (
                  <div className="text-[10px] text-violet-700 font-medium mt-1 flex items-center gap-1" data-testid={`receipt-suggested-account-${r._id}`} title="Konto-Vorschlag der KI aus dem Posteingang — wird beim Buchen verwendet, sofern keine Regel greift">
                    ✨ KI-Konto: {r.suggested_account}
                    {r.status !== "deleted" && (
                      <button
                        type="button"
                        onClick={() => onEditAccount?.(r)}
                        className="text-violet-400 hover:text-violet-800 transition-colors"
                        title="Konto ändern — gilt beim Buchen und im Bank-Abgleich, KI lernt daraus"
                        data-testid={`receipt-account-edit-${r._id}`}
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                )}
                {r.quality_status === "missing_amount" && (
                  <div className="text-[10px] text-amber-700 font-medium mt-1">
                    ⚠️ Betrag prüfen
                  </div>
                )}
                {r.quality_status === "low_quality" && (
                  <div className="text-[10px] text-amber-700 font-medium mt-1">
                    ⚠️ Foto schwer lesbar
                  </div>
                )}
              </div>
            </TableCell>
            <TableCell>
              <div className="font-medium">{r.amount.toFixed(2)} {r.currency}</div>
              {r.original_currency && r.original_currency !== r.currency && (
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Original: <b>{Number(r.original_amount).toFixed(2)} {r.original_currency}</b>
                  {r.fx_rate && <> · Kurs {Number(r.fx_rate).toFixed(4)}</>}
                </div>
              )}
            </TableCell>
            <TableCell>{taxRatePct(r.tax_rate)}</TableCell>
            <TableCell>{statusLabel(r.status)}</TableCell>
            <TableCell>
              {r.duplicate_signature && (
                <button
                  type="button"
                  onClick={() => onOpenMerger(r._id)}
                  className="cursor-pointer hover:opacity-80"
                  data-testid={`duplicate-warning-${r._id}`}
                  title="Zum KI-Merger — Duplikate zusammenlegen"
                >
                  <Badge
                    variant="outline"
                    className="border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100"
                    data-testid="duplicate-warning"
                  >
                    Mögliches Duplikat →
                  </Badge>
                </button>
              )}
              {r.duplicate_signature && onCompareDuplicates && (
                <button
                  type="button"
                  onClick={() => onCompareDuplicates(r._id)}
                  className="ml-1 inline-flex items-center rounded border border-amber-300 bg-white p-1 text-amber-700 hover:bg-amber-100 align-middle"
                  data-testid={`duplicate-compare-${r._id}`}
                  title="Lupe: alle Duplikate gross nebeneinander anzeigen"
                >
                  <ZoomIn className="h-3.5 w-3.5" />
                </button>
              )}
            </TableCell>
            <TableCell className="space-x-2">
              {r.status === "archived" ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onUnarchive?.(r._id)}
                  className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  data-testid={`receipt-unarchive-button-${r._id}`}
                >
                  <Undo2 className="h-3.5 w-3.5 mr-1" />
                  Wiederherstellen
                </Button>
              ) : null}
              {r.status === "deleted" ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRestore?.(r._id)}
                  className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  data-testid={`receipt-restore-button-${r._id}`}
                  title="Beleg aus dem Papierkorb zurückholen (Status: Offen)"
                >
                  <Undo2 className="h-3.5 w-3.5 mr-1" />
                  Wiederherstellen
                </Button>
              ) : null}
              {r.status !== "booked" && r.status !== "archived" && r.status !== "deleted" && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPost(r._id)}
                    data-testid={`receipt-post-button-${r._id}`}
                  >
                    Buchen
                  </Button>
                  {onToInbox && !r.journal_entry_id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onToInbox(r)}
                      className="text-slate-600"
                      title="Zurück in den Posteingang – Datum, Art und Konto neu zuordnen"
                      data-testid={`receipt-to-inbox-button-${r._id}`}
                    >
                      <Inbox className="h-3.5 w-3.5 mr-1" />
                      Posteingang
                    </Button>
                  )}
                  {reclaimCompanies?.length > 0 && <ReclaimBusinessAction companyId={r.company_id} receipt={r} companies={reclaimCompanies} onDone={onReclaimed} compact />}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onReanalyze(r._id)}
                    disabled={reanalyzingId === r._id}
                    data-testid={`receipt-reanalyze-button-${r._id}`}
                  >
                    <RefreshCw className={`h-3.5 w-3.5 mr-1 ${reanalyzingId === r._id ? "animate-spin" : ""}`} />
                    {reanalyzingId === r._id ? "Analysiere…" : "Neu analysieren"}
                  </Button>
                </>
              )}
              {r.status === "booked" && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRebook(r)}
                    className="text-violet-700 border-violet-300 hover:bg-violet-50"
                    data-testid={`receipt-rebook-button-${r._id}`}
                  >
                    <ArrowLeftRight className="h-3.5 w-3.5 mr-1" />
                    Umbuchen
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onUnbook(r._id)}
                    disabled={unbookingId === r._id}
                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    data-testid={`receipt-unbook-button-${r._id}`}
                  >
                    <Undo2 className="h-3.5 w-3.5 mr-1" />
                    {unbookingId === r._id ? "Buche zurück…" : "Rückverbuchen"}
                  </Button>
                  {onToInboxBooked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onToInboxBooked(r)}
                      className="text-slate-600"
                      title="Buchung rückgängig machen und Beleg in den Posteingang legen – Datum, Art und Konto neu zuordnen"
                      data-testid={`receipt-unbook-to-inbox-button-${r._id}`}
                    >
                      <Inbox className="h-3.5 w-3.5 mr-1" />
                      Posteingang
                    </Button>
                  )}
                  {reclaimCompanies?.length > 0 && <ReclaimBusinessAction companyId={r.company_id} receipt={r} companies={reclaimCompanies} onDone={onReclaimed} compact />}
                </>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
